class HaloVariantMedia extends HTMLElement {
  #abortController = new AbortController();
  #finish = 'blackout';
  #heroVideo;
  #heroVideoReady = false;
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #desktop = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    document.body.classList.add('halo-product-page');

    const { signal } = this.#abortController;
    document.addEventListener('variant:update', this.#handleVariantUpdate, { signal });
    document.addEventListener('change', this.#handleControlChange, { signal });
    this.#reduceMotion.addEventListener('change', this.#handleMediaPreference, { signal });
    this.#desktop.addEventListener('change', this.#handleMediaPreference, { signal });

    this.#heroVideo = this.querySelector('[data-halo-hero-video]');
    if (this.#heroVideo) {
      this.#heroVideo.addEventListener('playing', this.#handleHeroPlaying, { signal });
      this.#heroVideo.addEventListener('error', this.#handleHeroError, { signal });
    }

    this.#updateFinish(this.#initialFinish());
  }

  disconnectedCallback() {
    this.#abortController.abort();
    document.body.classList.remove('halo-product-page');
  }

  #handleVariantUpdate = (event) => {
    this.#updateFinish(this.#finishFromVariant(event.detail?.resource));
  };

  #handleControlChange = (event) => {
    const control = event.target;
    if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
    if (!control.closest('variant-picker')) return;

    const selectedValues = Array.from(
      document.querySelectorAll('variant-picker input:checked, variant-picker select option:checked')
    ).map((option) => option.value || option.textContent || '');

    this.#updateFinish(this.#matchFinish(selectedValues));
  };

  #handleMediaPreference = () => {
    if (!this.#canPlayHeroVideo()) {
      this.#showHeroFallback();
      return;
    }
    this.#playHeroVideo();
  };

  #handleHeroPlaying = () => {
    this.#heroVideoReady = true;
    this.#heroVideo?.classList.add('is-ready');
    this.querySelector('.halo-hero__media')?.classList.add('has-ready-video');
  };

  #handleHeroError = () => {
    this.#heroVideoReady = false;
    this.#showHeroFallback();
  };

  #initialFinish() {
    try {
      return this.#finishFromVariant(JSON.parse(this.dataset.initialVariant || '{}'));
    } catch (_error) {
      return this.dataset.defaultFinish || 'blackout';
    }
  }

  #finishFromVariant(variant) {
    if (!variant) return this.dataset.defaultFinish || 'blackout';

    const values = [variant.title, variant.option1, variant.option2, variant.option3];
    if (Array.isArray(variant.options)) values.push(...variant.options);

    return this.#matchFinish(values);
  }

  #matchFinish(values) {
    const spectrumValue = (this.dataset.spectrumValue || 'Spectrum').toLocaleLowerCase();
    const blackoutValue = (this.dataset.blackoutValue || 'Blackout').toLocaleLowerCase();
    const normalized = values.filter(Boolean).join(' ').toLocaleLowerCase();

    if (normalized.includes(spectrumValue)) return 'spectrum';
    if (normalized.includes(blackoutValue)) return 'blackout';
    return this.dataset.defaultFinish || 'blackout';
  }

  #updateFinish(finish) {
    const safeFinish = finish === 'spectrum' ? 'spectrum' : 'blackout';
    this.#finish = safeFinish;
    document.body.dataset.haloFinish = safeFinish;

    document.querySelectorAll('[data-halo-variant-media]').forEach((media) => {
      const isActive = media.getAttribute('data-halo-variant') === safeFinish;
      media.toggleAttribute('hidden', !isActive);
      media.setAttribute('aria-hidden', String(!isActive));
    });

    document.querySelectorAll('[data-halo-finish-label]').forEach((label) => {
      label.textContent = safeFinish === 'spectrum'
        ? this.dataset.spectrumValue || 'Spectrum'
        : this.dataset.blackoutValue || 'Blackout';
    });

    if (this.#canPlayHeroVideo()) this.#playHeroVideo();
    else this.#showHeroFallback();

    document.dispatchEvent(new CustomEvent('halo:finishchange', { detail: { finish: safeFinish } }));
  }

  #canPlayHeroVideo() {
    return Boolean(
      this.#heroVideo &&
      this.#finish === 'blackout' &&
      this.#desktop.matches &&
      !this.#reduceMotion.matches
    );
  }

  #playHeroVideo() {
    if (!this.#canPlayHeroVideo()) return;
    const video = this.#heroVideo;
    const source = video.dataset.haloHeroVideoSrc;
    if (!source) return;

    video.hidden = false;
    if (!video.getAttribute('src')) {
      video.setAttribute('src', source);
      video.load();
    }

    if (video.ended) {
      this.querySelector('.halo-hero__media')?.classList.toggle('has-ready-video', this.#heroVideoReady);
      return;
    }

    video.play().catch(() => this.#showHeroFallback());
  }

  #showHeroFallback() {
    const video = this.#heroVideo;
    this.querySelector('.halo-hero__media')?.classList.remove('has-ready-video');
    video?.pause();
  }
}

class HaloProductGallery extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener('halo:finishchange', this.#handleFinishChange, { signal });

    this.querySelectorAll('[data-halo-product-gallery-series]').forEach((series) => {
      this.#select(series, 0);
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleClick = (event) => {
    const navigation = event.target.closest('[data-halo-gallery-navigation]');
    if (navigation && this.contains(navigation)) {
      const series = navigation.closest('[data-halo-product-gallery-series]');
      if (!series) return;

      const thumbnails = Array.from(series.querySelectorAll('[data-halo-gallery-thumbnail]'));
      const currentIndex = Math.max(0, thumbnails.findIndex((thumbnail) => thumbnail.hasAttribute('aria-current')));
      const direction = navigation.dataset.haloGalleryNavigation === 'previous' ? -1 : 1;
      this.#select(series, currentIndex + direction, true);
      return;
    }

    const thumbnail = event.target.closest('[data-halo-gallery-thumbnail]');
    if (!thumbnail || !this.contains(thumbnail)) return;

    const series = thumbnail.closest('[data-halo-product-gallery-series]');
    if (!series) return;

    this.#select(series, Number(thumbnail.dataset.index || 0), true);
  };

  #handleFinishChange = (event) => {
    const finish = event.detail?.finish;
    const series = this.querySelector(`[data-halo-product-gallery-series][data-halo-variant="${finish}"]`);
    if (series) this.#select(series, 0);
  };

  #select(series, requestedIndex, shouldScroll = false) {
    const slides = Array.from(series.querySelectorAll('[data-halo-gallery-slide]'));
    const thumbnails = Array.from(series.querySelectorAll('[data-halo-gallery-thumbnail]'));
    if (slides.length === 0) return;

    const index = Math.max(0, Math.min(slides.length - 1, requestedIndex));
    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.toggleAttribute('hidden', !isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });
    thumbnails.forEach((thumbnail, thumbnailIndex) => {
      thumbnail.toggleAttribute('aria-current', thumbnailIndex === index);
    });

    const previous = series.querySelector('[data-halo-gallery-navigation="previous"]');
    const next = series.querySelector('[data-halo-gallery-navigation="next"]');
    if (previous) previous.disabled = index === 0;
    if (next) next.disabled = index === slides.length - 1;

    if (shouldScroll) {
      thumbnails[index]?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }
}

class HaloTechCarousel extends HTMLElement {
  #abortController = new AbortController();
  #track;
  #slides = [];
  #previous;
  #next;
  #count;
  #activeIndex = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-halo-carousel-track]');
    this.#slides = Array.from(this.querySelectorAll('[data-halo-carousel-slide]'));
    this.#previous = this.querySelector('[data-halo-carousel-previous]');
    this.#next = this.querySelector('[data-halo-carousel-next]');
    this.#count = this.querySelector('[data-halo-carousel-count]');
    if (!this.#track || this.#slides.length === 0) return;

    const { signal } = this.#abortController;
    this.#previous?.addEventListener('click', () => this.#move(-1), { signal });
    this.#next?.addEventListener('click', () => this.#move(1), { signal });
    this.#track.addEventListener('scroll', this.#handleScroll, { signal, passive: true });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.#updateControls();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#move(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#move(1);
    }
  };

  #handleScroll = () => {
    window.requestAnimationFrame(() => {
      const trackCenter = this.#track.scrollLeft + this.#track.clientWidth / 2;
      let closest = 0;
      let distance = Number.POSITIVE_INFINITY;
      this.#slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const candidate = Math.abs(slideCenter - trackCenter);
        if (candidate < distance) {
          closest = index;
          distance = candidate;
        }
      });
      this.#activeIndex = closest;
      this.#updateControls();
    });
  };

  #move(direction) {
    const targetIndex = Math.max(0, Math.min(this.#slides.length - 1, this.#activeIndex + direction));
    const target = this.#slides[targetIndex];
    if (!target) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({
      left: target.offsetLeft - (this.#track.clientWidth - target.offsetWidth) / 2,
      behavior: reducedMotion ? 'auto' : 'smooth'
    });
    this.#activeIndex = targetIndex;
    this.#updateControls();
  }

  #updateControls() {
    if (this.#previous) {
      this.#previous.disabled = this.#activeIndex === 0;
      this.#previous.setAttribute('aria-disabled', String(this.#activeIndex === 0));
    }
    if (this.#next) {
      const atEnd = this.#activeIndex === this.#slides.length - 1;
      this.#next.disabled = atEnd;
      this.#next.setAttribute('aria-disabled', String(atEnd));
    }
    this.dataset.activeIndex = String(this.#activeIndex);
    if (this.#count) {
      this.#count.textContent = `${String(this.#activeIndex + 1).padStart(2, '0')} / ${String(this.#slides.length).padStart(2, '0')}`;
    }
  }
}

class HaloVideoWall extends HTMLElement {
  #abortController = new AbortController();
  #activeCard;
  #hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    const { signal } = this.#abortController;
    this.querySelectorAll('[data-halo-video-card]').forEach((card) => {
      card.addEventListener('pointerenter', () => {
        if (this.#hoverCapable.matches && !this.#reduceMotion.matches) this.#play(card);
      }, { signal });
      card.addEventListener('pointerleave', () => {
        if (this.#hoverCapable.matches) this.#reset(card);
      }, { signal });
      card.addEventListener('click', (event) => {
        if (this.#hoverCapable.matches && event.detail > 0) return;
        this.#toggle(card);
      }, { signal });
      card.querySelector('video')?.addEventListener('ended', () => this.#reset(card), { signal });
    });
    document.addEventListener('visibilitychange', this.#handleVisibility, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    if (this.#activeCard) this.#reset(this.#activeCard);
  }

  #handleVisibility = () => {
    if (document.hidden && this.#activeCard) this.#reset(this.#activeCard);
  };

  #toggle(card) {
    if (card === this.#activeCard && card.dataset.playing === 'true') this.#reset(card);
    else this.#play(card);
  }

  #play(card) {
    if (this.#activeCard && this.#activeCard !== card) this.#reset(this.#activeCard);
    const video = card.querySelector('video');
    const source = video?.dataset.haloVideoSrc;
    if (!video || !source) return;

    if (!video.getAttribute('src')) {
      video.setAttribute('src', source);
      video.load();
    }

    this.#activeCard = card;
    video.play().then(() => {
      card.dataset.playing = 'true';
      card.setAttribute('aria-pressed', 'true');
    }).catch(() => this.#reset(card));
  }

  #reset(card) {
    const video = card?.querySelector('video');
    if (video) {
      video.pause();
      try { video.currentTime = 0; } catch (_error) { /* Keep poster visible. */ }
    }
    card?.removeAttribute('data-playing');
    card?.setAttribute('aria-pressed', 'false');
    if (this.#activeCard === card) this.#activeCard = undefined;
  }
}

class HaloOptionGuide extends HTMLElement {
  #abortController = new AbortController();
  #scope;
  #variants = [];
  #observer;

  connectedCallback() {
    this.#scope = this.closest('.product-details') || this.parentElement;
    if (!this.#scope) return;

    const { signal } = this.#abortController;
    try {
      this.#variants = JSON.parse(this.querySelector('[data-halo-variants-json]')?.textContent || '[]');
    } catch (_error) {
      this.#variants = [];
    }
    this.#scope.addEventListener('change', this.#sync, { signal });
    document.addEventListener('variant:update', this.#sync, { signal });
    this.#observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.addedNodes.length || mutation.removedNodes.length)) {
        this.#sync();
      }
    });
    this.#observer.observe(this.#scope, { childList: true, subtree: true });
    this.#sync();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#observer?.disconnect();
  }

  #sync = () => {
    window.requestAnimationFrame(() => {
      const optionIndex = this.dataset.optionIndex;
      if (optionIndex === undefined) return;

      const fieldset = this.#scope.querySelector(`fieldset[data-fieldset-index="${optionIndex}"]`);
      const displayName = this.dataset.displayName;
      const legend = fieldset?.querySelector(':scope > legend');
      if (legend && displayName) {
        const textNode = Array.from(legend.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
        if (textNode) textNode.textContent = `${displayName} `;
      }

      const selectedInput = fieldset?.querySelector('input:checked');
      const optionName = this.dataset.optionName;
      const selectedDropdown = optionName
        ? this.#scope.querySelector(`select[name="options[${CSS.escape(optionName)}]"]`)
        : null;
      const value = String(selectedInput?.value || selectedDropdown?.value || '').toLowerCase();
      const selectedKey = value.startsWith('hybrid') ? 'hybrid' : value.startsWith('custom') ? 'custom' : 'preset';

      this.querySelectorAll('[data-halo-option-copy]').forEach((copy) => {
        copy.hidden = copy.dataset.haloOptionCopy !== selectedKey;
      });

      const selectedOptions = Array.from(this.#scope.querySelectorAll('variant-picker fieldset'))
        .sort((a, b) => Number(a.dataset.fieldsetIndex || 0) - Number(b.dataset.fieldsetIndex || 0))
        .map((option) => option.querySelector('input:checked')?.value || '');
      const selectedVariant = this.#variants.find((variant) => (
        Array.isArray(variant.options) &&
        variant.options.length === selectedOptions.length &&
        variant.options.every((option, index) => option === selectedOptions[index])
      ));

      if (!selectedVariant) return;

      const variantTitle = this.querySelector('[data-halo-selected-variant]');
      if (variantTitle) variantTitle.textContent = selectedVariant.title || '';

      const isOrderable = selectedVariant.inventory_policy === 'continue';
      const orderability = this.querySelector('[data-halo-orderability]');
      if (orderability) {
        orderability.textContent = isOrderable
          ? this.dataset.orderableLabel || ''
          : this.dataset.notOrderableLabel || '';
      }

      const syncVariantInputs = () => {
        if (!this.isConnected) return;
        this.#scope.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
          input.value = selectedVariant.id;
          input.setAttribute('value', selectedVariant.id);
        });
      };

      const normalizeSwedishPrice = () => {
        if (!document.documentElement.lang.toLowerCase().startsWith('sv')) return;
        this.#scope.querySelectorAll('product-price .price').forEach((price) => {
          const walker = document.createTreeWalker(price, NodeFilter.SHOW_TEXT);
          let node;
          while ((node = walker.nextNode())) {
            node.nodeValue = node.nodeValue.replace(/(\d),(?=\d{3}\b)/g, '$1\u00a0');
          }
        });
      };

      syncVariantInputs();
      normalizeSwedishPrice();
      [0, 80, 240].forEach((delay) => {
        window.setTimeout(() => {
          syncVariantInputs();
          normalizeSwedishPrice();
        }, delay);
      });
    });
  };
}

if (!customElements.get('halo-variant-media')) {
  customElements.define('halo-variant-media', HaloVariantMedia);
}
if (!customElements.get('halo-tech-carousel')) {
  customElements.define('halo-tech-carousel', HaloTechCarousel);
}
if (!customElements.get('halo-product-gallery')) {
  customElements.define('halo-product-gallery', HaloProductGallery);
}
if (!customElements.get('halo-video-wall')) {
  customElements.define('halo-video-wall', HaloVideoWall);
}
if (!customElements.get('halo-option-guide')) {
  customElements.define('halo-option-guide', HaloOptionGuide);
}
